import { useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
    SandpackProvider,
    SandpackLayout,
    SandpackPreview,
    useSandpack,
    SandpackCodeEditor,
    SandpackFileExplorer,
} from '@codesandbox/sandpack-react';
import { SessionContext } from '@contexts/SessionProvider';
import { Button, Tooltip } from '@appkit4/react-components';
import SandpackErrorOverlay from '@components/SandpackErrorOverlay/SandpackErrorOverlay';
import { classifyErrors, SandpackError, ErrorClassification, generateFixPrompt } from '@utils/errorClassifier';
import { injectFontFix, SANDPACK_EXTERNAL_RESOURCES } from '@utils/sandpackAssets';
import { SANDBOX_BUNDLER_URL } from '@config/sandbox';
import './SandpackRenderer.scss';

interface EnhancedSandpackRendererProps {
    files: Record<string, any>;
    dependencies: Record<string, string>;
    style?: Record<string, string>;
    'aria-hidden'?: boolean;
    isOptionSource?: boolean;
    showNavigator?: boolean;
    className?: string;
    onAutoFixRequest?: (prompt: string, attemptNumber: number, isRetryingOriginal: boolean) => void;
    maxAutoFixAttempts?: number;
    initialPrompt?: string;
    isInitialGeneration?: boolean;
}

const EnhancedSandpackRenderer = (props: EnhancedSandpackRendererProps) => {
    const {
        files,
        dependencies,
        isOptionSource,
        showNavigator,
        className,
        onAutoFixRequest,
        maxAutoFixAttempts = 3,
        initialPrompt = '',
        isInitialGeneration = false,
    } = props;
    const { devModeActive, templateVersion, designSystem } = useContext(SessionContext);
    const customSetup = useMemo(
        () => ({
            dependencies,
            npmRegistries: [
                {
                    enabledScopes: ['@appkit4', 'ckeditor5-appkit-build'],
                    limitToScopes: true,
                    registryUrl: `${window.location.protocol}//${window.location.host}/npm-proxy/`,
                    proxyEnabled: false,
                },
            ],
        }),
        [dependencies]
    );
    const [fullScreen, setFullScreen] = useState(false);
    const [errorState, setErrorState] = useState<{
        classification: ErrorClassification | null;
        attemptNumber: number;
        isFixing: boolean;
        isDismissed: boolean;
        isRetryingOriginal: boolean;
    }>({
        classification: null,
        attemptNumber: 0,
        isFixing: false,
        isDismissed: false,
        isRetryingOriginal: false,
    });

    useEffect(() => {
        const listener = () => setFullScreen(Boolean(document.fullscreenElement));
        document.addEventListener('fullscreenchange', listener);
        return () => document.removeEventListener('fullscreenchange', listener);
    }, []);

    const customFiles = useMemo(() => {
        let result = files;
        if (templateVersion === 'v2') {
            const remapped: Record<string, any> = {};
            for (const [key, value] of Object.entries(files)) {
                const normalizedKey = key.replace(/^\//, '');
                if (normalizedKey === 'main.tsx') {
                    remapped['/index.tsx'] = value;
                } else {
                    remapped[key] = value;
                }
            }
            result = remapped;
        }
        return designSystem === 'Appkit' || designSystem === 'AppkitRebrand' || designSystem === 'NewEra'
            ? injectFontFix(result, designSystem)
            : result;
    }, [JSON.stringify(files), templateVersion, designSystem]); // eslint-disable-line react-hooks/exhaustive-deps
    // Note: files is intentionally omitted; JSON.stringify is used for complex object memoization

    const handleFullScreen = async () => {
        const elem = document.getElementsByClassName('sandbox-container')[0] as HTMLElement | undefined;
        if (!elem) return;

        if (!document.fullscreenElement) {
            await elem.requestFullscreen();
            // Move focus to the Sandpack preview iframe so keyboard input works
            // without requiring a manual click inside the maximized window.
            const iframe = elem.querySelector('iframe') as HTMLIFrameElement | null;
            if (iframe) {
                requestAnimationFrame(() => iframe.focus());
            }
        } else {
            await document.exitFullscreen();
        }
    };

    const handleAutoFix = useCallback(
        (classification: ErrorClassification, attemptNumber: number) => {
            if (!onAutoFixRequest || attemptNumber > maxAutoFixAttempts) {
                return;
            }

            let prompt: string;
            let isRetryingOriginal = false;

            if (isInitialGeneration && initialPrompt) {
                if (attemptNumber === 1) {
                    prompt = initialPrompt;
                    isRetryingOriginal = true;
                } else if (attemptNumber === 2) {
                    prompt = generateFixPrompt(classification, attemptNumber);
                    isRetryingOriginal = false;
                } else {
                    prompt = `${initialPrompt}\n\nNote: Previous generation attempts had errors. Please ensure the code is complete and correct.`;
                    isRetryingOriginal = true;
                }
            } else {
                prompt = generateFixPrompt(classification, attemptNumber);
                isRetryingOriginal = false;
            }

            setErrorState((prev) => ({
                ...prev,
                isFixing: true,
                attemptNumber,
                isRetryingOriginal,
            }));

            onAutoFixRequest(prompt, attemptNumber, isRetryingOriginal);
        },
        [onAutoFixRequest, maxAutoFixAttempts, isInitialGeneration, initialPrompt]
    );

    const handleManualRetry = useCallback(() => {
        if (errorState.classification) {
            const nextAttempt = errorState.attemptNumber + 1;
            handleAutoFix(errorState.classification, nextAttempt);
        }
    }, [errorState, handleAutoFix]);

    const handleDismiss = useCallback(() => {
        setErrorState((prev) => ({
            ...prev,
            isDismissed: true,
        }));
    }, []);

    const showErrorOverlay =
        errorState.classification &&
        !errorState.isDismissed &&
        (!errorState.classification.isRecoverable || errorState.attemptNumber >= maxAutoFixAttempts);

    return (
        <SandpackProvider
            className={className}
            template={templateVersion === 'v2' ? 'react-ts' : 'react'}
            files={customFiles}
            customSetup={customSetup}
            options={{
                externalResources:
                    designSystem === 'Appkit' || designSystem === 'AppkitRebrand' || designSystem === 'NewEra'
                        ? SANDPACK_EXTERNAL_RESOURCES
                        : [],
                bundlerTimeOut: 300_000,
                classes: {
                    'sp-wrapper': isOptionSource ? 'non-current-wrapper' : 'current-wrapper',
                },
                recompileDelay: 1000,
                bundlerURL: SANDBOX_BUNDLER_URL,
            }}>
            <div style={{ position: 'relative', height: '100%', width: '100%' }}>
                <SandpackLayout>
                    {devModeActive && (
                        <>
                            <SandpackFileExplorer />
                            <SandpackCodeEditor showLineNumbers showTabs closableTabs />
                        </>
                    )}
                    <SandpackPreview
                        showOpenInCodeSandbox={false}
                        showNavigator={showNavigator}
                        showSandpackErrorOverlay={false}
                        style={{ display: devModeActive ? 'none' : 'flex' }}
                    />
                    <EnhancedSandpackListener
                        onError={(classification) => {
                            setErrorState((prev) => {
                                // If user already dismissed an error of the same type,
                                // don't un-dismiss it — preserve the dismissal.
                                const isSameErrorType =
                                    prev.isDismissed && prev.classification?.errorType === classification.errorType;
                                return {
                                    classification,
                                    attemptNumber: isSameErrorType ? prev.attemptNumber : 1,
                                    isFixing: false,
                                    isDismissed: isSameErrorType ? true : false,
                                    isRetryingOriginal: false,
                                };
                            });

                            if (classification.isRecoverable) {
                                // Only trigger auto-fix if not already dismissed
                                setErrorState((prev) => {
                                    if (!prev.isDismissed) {
                                        handleAutoFix(classification, 1);
                                    }
                                    return prev;
                                });
                            }
                        }}
                        onCompilationSuccess={() => {
                            setErrorState({
                                classification: null,
                                attemptNumber: 0,
                                isFixing: false,
                                isDismissed: false,
                                isRetryingOriginal: false,
                            });
                        }}
                        onNetworkError={(errorMessage) => {
                            const classification: ErrorClassification = {
                                isRecoverable: false,
                                errorType: 'network',
                                confidenceScore: 0.9,
                                context: errorMessage,
                            };
                            setErrorState({
                                classification,
                                attemptNumber: 1,
                                isFixing: false,
                                isDismissed: false,
                                isRetryingOriginal: false,
                            });
                        }}
                    />
                </SandpackLayout>

                {showErrorOverlay && errorState.classification && (
                    <SandpackErrorOverlay
                        classification={errorState.classification}
                        isFixing={errorState.isFixing}
                        attemptNumber={errorState.attemptNumber}
                        maxAttempts={maxAutoFixAttempts}
                        isRetryingOriginal={errorState.isRetryingOriginal}
                        onManualRetry={handleManualRetry}
                        onDismiss={handleDismiss}
                    />
                )}

                {fullScreen && (
                    <Tooltip trigger="hover" distance={4} id="share-control" content="Full screen">
                        <Button
                            kind={'text'}
                            icon="icon-minimize-outline"
                            onClick={handleFullScreen}
                            aria-label="Exit full screen button"
                            className="exit-full-screen-button"
                        />
                    </Tooltip>
                )}
            </div>
        </SandpackProvider>
    );
};

interface EnhancedSandpackListenerProps {
    onError: (classification: ErrorClassification) => void;
    onCompilationSuccess: () => void;
    onNetworkError: (errorMessage: string) => void;
}

const EnhancedSandpackListener = ({ onError, onCompilationSuccess, onNetworkError }: EnhancedSandpackListenerProps) => {
    const { listen } = useSandpack();
    const errorsRef = useRef<SandpackError[]>([]);
    const networkErrorReported = useRef(false);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const onErrorRef = useRef(onError);
    const onCompilationSuccessRef = useRef(onCompilationSuccess);
    useEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);
    useEffect(() => {
        onCompilationSuccessRef.current = onCompilationSuccess;
    }, [onCompilationSuccess]);

    const reportErrors = useCallback(() => {
        if (errorsRef.current.length === 0) return;
        const classification = classifyErrors(errorsRef.current);
        errorsRef.current = [];
        onErrorRef.current(classification);
    }, []);

    const scheduleReport = useCallback(() => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(reportErrors, 2000);
    }, [reportErrors]);

    const addUniqueError = useCallback(
        (error: SandpackError) => {
            const msg = (error.message || '').trim();
            if (!msg) return;

            const firstLine = msg.split('\n')[0].trim();

            const isNoise =
                /^Warning:/i.test(firstLine) ||
                /^The above error occurred in/i.test(firstLine) ||
                /^%s/i.test(firstLine) ||
                /Failed %s/i.test(firstLine) ||
                /_csb/i.test(firstLine);
            if (isNoise) return;

            const isDuplicate = errorsRef.current.some((e) => {
                const existingCore = (e.message || '').split('\n')[0].trim();
                return existingCore === firstLine;
            });
            if (!isDuplicate) {
                errorsRef.current = [...errorsRef.current, error];
                scheduleReport();
            }
        },
        [scheduleReport]
    );

    useEffect(() => {
        const handleOnline = () => {
            networkErrorReported.current = false;
        };

        const handleOffline = () => {
            if (!networkErrorReported.current) {
                networkErrorReported.current = true;
                onNetworkError('Network connection lost. Please check your internet connection and try again.');
            }
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [onNetworkError]);

    useEffect(() => {
        const stopListening = listen((msg: any) => {
            if (msg.type === 'action' && msg.action === 'notification') {
                if (msg.notificationType === 'error' || msg.status === 'error') {
                    addUniqueError({
                        type: 'error',
                        message: msg.title || msg.message || '',
                        title: msg.title,
                        severity: 'error',
                    });
                }
            } else if (msg.severity && msg.severity === 'error') {
                addUniqueError({
                    type: 'error',
                    message: msg.message || '',
                    title: msg.title,
                    line: msg.line,
                    column: msg.column,
                    path: msg.path,
                    severity: msg.severity,
                });
            } else if (msg.type === 'console' && msg.log) {
                msg.log.forEach((logItem: any) => {
                    if (logItem.method === 'error') {
                        let errorMessage = '';
                        const data = logItem.data?.[0];

                        if (typeof data === 'string') {
                            errorMessage = data;
                        } else if (data && typeof data === 'object') {
                            errorMessage = data.message || data.toString() || JSON.stringify(data);
                        } else {
                            errorMessage = String(data || '');
                        }

                        addUniqueError({
                            type: 'runtime',
                            message: errorMessage,
                            severity: 'error',
                        });
                    }
                });
            }

            if (msg.type === 'done') {
                const hasCompilationError = msg.compilatonError || msg.compilationError;

                if (hasCompilationError && errorsRef.current.length > 0) {
                    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
                    reportErrors();
                } else if (!hasCompilationError) {
                    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
                    errorsRef.current = [];
                    onCompilationSuccessRef.current();
                }
            }
        });

        return () => stopListening();
    }, [listen, addUniqueError, reportErrors]);

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, []);

    return null;
};

export default EnhancedSandpackRenderer;
